import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { FeaturedItem } from '@/types/moodboards';

interface FeaturedGridItemProps {
  item: FeaturedItem;
}

export function FeaturedGridItem({ item }: FeaturedGridItemProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (item.item_type === 'moodboard' && item.item_id) {
      navigate(`/moodboard/${item.item_id}`);
    } else if (item.item_type === 'album' && item.item_id) {
      navigate(`/album/${item.item_id}`);
    } else if (item.item_type === 'loop' && item.content_id) {
      navigate(`/loops/${item.content_id}`);
    } else if (item.item_type === 'media' && item.content_id) {
      // Open lightbox or navigate to original post
      if (item.content_type === 'bulletin') {
        navigate(`/bulletins/${item.content_id}`);
      } else if (item.content_type === 'loop') {
        navigate(`/loops/${item.content_id}`);
      }
    }
  };

  const getItemContent = () => {
    // For direct media
    if (item.media_url) {
      return (
        <img
          src={item.media_url}
          alt="Featured item"
          className="w-full h-full object-cover"
        />
      );
    }

    // Placeholder for albums/moodboards - you'll want to fetch their cover images
    return (
      <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
        <span className="text-4xl">
          {item.item_type === 'moodboard' && '🎨'}
          {item.item_type === 'album' && '📁'}
          {item.item_type === 'loop' && '🔁'}
        </span>
      </div>
    );
  };

  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleClick}
      className="relative w-full h-full rounded-2xl overflow-hidden group shadow-lg"
    >
      {/* Content */}
      {getItemContent()}

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="absolute top-3 right-3">
          <div className="p-2 rounded-full bg-white/20 backdrop-blur-sm">
            <ExternalLink className="w-4 h-4 text-white" />
          </div>
        </div>

        <div className="absolute bottom-3 left-3 right-3">
          <p className="text-white text-sm font-medium capitalize">
            {item.item_type}
          </p>
        </div>
      </div>
    </motion.button>
  );
}
